import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import CloudMonitoringPanels from "../../components/compliance-center/CloudMonitoringPanels";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

export default function ComplianceCenterAutomationWorkspace() {
  const { t } = useI18n();
  const { token } = useAuth();
  const [counts, setCounts] = useState({ rules: 0, drift: 0, requests: 0, connectors: 0, runs: 0, escalations: 0 });

  useEffect(() => {
    let active = true;
    const load = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      const [rulesRes, driftRes, requestsRes, connectorsRes, runsRes, escalationsRes] = await Promise.all([
        fetch(API_ENDPOINTS.COMPLIANCE_AUTOMATION_RULES, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_DRIFT_EVENTS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_AUTOMATED_EVIDENCE_REQUESTS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_CONNECTORS, { headers }),
        fetch(API_ENDPOINTS.WORKFLOW_RUNS, { headers }),
        fetch(API_ENDPOINTS.WORKFLOW_ESCALATIONS, { headers }),
      ]);
      const count = async (res: Response) => ((res.ok ? (await res.json().catch(() => ({})))?.data : []) || []).length || 0;
      if (!active) return;
      setCounts({
        rules: await count(rulesRes),
        drift: await count(driftRes),
        requests: await count(requestsRes),
        connectors: await count(connectorsRes),
        runs: await count(runsRes),
        escalations: await count(escalationsRes),
      });
    };
    void load();
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <div className={listStyles.pageStack}>
      <CloudMonitoringPanels token={token || ""} />
      <details className={listStyles.panel}>
        <summary className={listStyles.panelTitle}>{t("gcpSummary.automationCatalog", "Compliance automation catalog")}</summary>
        <p className={listStyles.panelSubtitle}>
          {t(
            "gcpSummary.workflowScope",
            "Connectors and workflow runs count the compliance automation catalog. They are not cloud sync runs.",
          )}
        </p>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid6}`}>
          <ComplianceCenterMetricTile label={t("complianceCenterUi.automationRules", "Automation rules")} value={counts.rules} tone="blue" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.driftEvents", "Drift events")} value={counts.drift} tone="orange" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.evidenceRequests", "Evidence requests")} value={counts.requests} tone="yellow" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.connectors", "Connectors")} value={counts.connectors} tone="green" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.workflowRuns", "Workflow runs")} value={counts.runs} tone="purple" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.escalations", "Escalations")} value={counts.escalations} tone="red" />
        </div>
      </details>
    </div>
  );
}
